<script lang="ts" setup>
import { ref, computed, watch, onMounted, toRaw } from 'vue'

import RoomieProgressBar from '@lahaus-roomie/roomie/src/components/RoomieProgressBar/index.vue'
import IconLeft from '@lahaus-roomie/roomie/src/assets/icons/left.svg'

import type { OnboardingFilters } from '../types'
import type { Props } from './types'
import { getLocationTitle } from '@/utils/locations'

import OnboardingStepsOpportunityType from '@/components/OnboardingContent/Steps/OpportunityType/index.vue'
import OnboardingStepsDeliveryTerm from '@/components/OnboardingContent/Steps/DeliveryTerm/index.vue'
import OnboardingStepsLoader from '@/components/OnboardingContent/Steps/Loader/index.vue'
import OnboardingStepsBedroomsNumber from '@/components/OnboardingContent/Steps/BedroomsNumber/index.vue'

const ANSWERS_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const { $trackPage } = useNuxtApp()

const currentStepIndex = ref<number>(parseInt(route.query.step as string))
const currentFilters = ref<OnboardingFilters>({ ...props.initialFilters })

const hasCompletedOnboarding = useCookie<boolean>('has_completed_onboarding', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const opportunityTypeCookie = useCookie<string | null>('onboarding_opportunity_type', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const deliveryTermCookie = useCookie<string | null>('onboarding_delivery_term', { maxAge: ANSWERS_COOKIE_MAX_AGE })
const bedroomsNumberCookie = useCookie<number | null>('onboarding_bedrooms_number', { maxAge: ANSWERS_COOKIE_MAX_AGE })

const locationsTitle = computed(() => {
  return getLocationTitle(route.query.custom_title, toRaw(currentFilters.value), t, locale.value)
})

const syncStepQueryParam = async () => {
  await router.replace({ query: { ...route.query, step: currentStepIndex.value.toString() } })

  // @ts-ignore
  $trackPage()
}

const syncFiltersQueryParams = async () => {
  const nextQueryParams: any = { ...route.query }

  if (currentFilters.value.clusters?.length) {
    const value = currentFilters.value.clusters.join('~')
    nextQueryParams.clusters = value
  }

  nextQueryParams.opportunity_type = currentFilters.value.opportunityType ?? null
  nextQueryParams.delivery_term = currentFilters.value.deliveryTerm ?? null
  nextQueryParams.bedrooms_number = currentFilters.value.bedroomsNumber ? currentFilters.value.bedroomsNumber.toString() : null

  for (const key in nextQueryParams) {
    if (!nextQueryParams[key]) {
      delete nextQueryParams[key]
    }
  }

  opportunityTypeCookie.value = currentFilters.value.opportunityType
  deliveryTermCookie.value = currentFilters.value.deliveryTerm
  bedroomsNumberCookie.value = currentFilters.value.bedroomsNumber

  await router.replace({ query: nextQueryParams })
}

const handleStepSubmit = async ({ name, value }: { name: string, value: string }) => {
  (currentFilters.value as any)[name] = value
  await syncFiltersQueryParams()
  currentStepIndex.value += 1
  await syncStepQueryParam()
}

const goToPreviousStep = async () => {
  currentStepIndex.value -= 1
  await syncStepQueryParam()
}

const handleSkip = async ({ name, value }: { name: string; value: string | null }) => {
  currentStepIndex.value += 1
  currentFilters.value[name] = value
  await syncFiltersQueryParams()
  await syncStepQueryParam()
}

const isOpportunityVisible = computed(() => currentStepIndex.value === 1)

const isDeliveryTermVisible = computed(() => currentStepIndex.value === 2)

const isBedroomsNumberVisible = computed(() => {
  return (currentFilters.value.opportunityType === 'investing' || !currentFilters.value.opportunityType)
    ? false
    : currentStepIndex.value === 3
})

const isReturnButtonVisible = computed(() => currentStepIndex.value > 1 && !props.loading)

const progressValue = computed(() => {
  const divider = currentFilters.value.opportunityType === 'investing' ? 4 : 5

  return ((currentStepIndex.value + 1) / divider) * 100
})

const goToSearchPage = () => {
  navigateTo({
    path: '/venta/propiedades',
    query: { ...route.query }
  }, { external: true })
}

watch(() => props.initialFilters, (newInitialFilters) => {
  currentFilters.value = { ...newInitialFilters }
}, { deep: true })

watch(() => currentStepIndex.value, (nextStep) => {
  if (nextStep < 3) return

  const isInvestingFlow = currentFilters.value.opportunityType === 'investing' || !currentFilters.value.opportunityType
  const isLivingFlow = currentFilters.value.opportunityType === 'living'

  if (isInvestingFlow && nextStep === 3) {
    hasCompletedOnboarding.value = true
    return goToSearchPage()
  }

  if (isLivingFlow && nextStep === 4) {
    hasCompletedOnboarding.value = true
    return goToSearchPage()
  }
}, { immediate: true })

onMounted(async () => {
  const initialQueryParams: any = {}

  if (!route.query.step) {
    currentStepIndex.value = 1
    initialQueryParams.step = 1
  } else {
    currentStepIndex.value = parseInt(route.query.step as string) || 1
  }

  await router.replace({ query: { ...route.query, ...initialQueryParams } })
})
</script>

<script lang="ts">
export default {
  name: 'OnboardingStepsModal'
}
</script>

<template>
  <section class="onboarding-modal w-full overflow-hidden absolute top-0 left-0">
    <div class="onboarding-modal__bg w-[1980px] h-[950px]" />

    <LazyAppModal>
      <RoomieProgressBar
        :value="progressValue"
        size="sm"
        class="w-full absolute top-0 left-0" />

      <button
        v-if="isReturnButtonVisible"
        id="onboarding-modal-return-button"
        data-lh-id="onboarding-modal-return-button"
        type="button"
        :aria-label="t('ariaLabelReturn')"
        class="w-32 h-32 p-4 rounded-full bg-primary-50"
        @click="goToPreviousStep">
        <IconLeft class="pointer-events-none fill-primary-600" />
      </button>

      <div class="onboarding-modal-title w-full text-22 mt-16 mb-32 text-center truncate">
        <p class="text-carbon-800">
          {{ t('title') }}
        </p>

        <p
          class="text-brand-800 truncate"
          :title="locationsTitle">
          {{ locationsTitle }}
        </p>
      </div>

      <div>
        <OnboardingStepsOpportunityType
          v-if="isOpportunityVisible"
          :value="currentFilters.opportunityType"
          @submit="handleStepSubmit"
          @skip="handleSkip" />

        <OnboardingStepsDeliveryTerm
          v-if="isDeliveryTermVisible"
          :value="currentFilters.deliveryTerm"
          @submit="handleStepSubmit"
          @skip="handleSkip" />

        <OnboardingStepsBedroomsNumber
          v-if="isBedroomsNumberVisible"
          id="onboarding-modal-return-button"
          :value="currentFilters.bedroomsNumber"
          @submit="handleStepSubmit"
          @skip="handleSkip" />

        <OnboardingStepsLoader v-if="loading" />
      </div>
    </LazyAppModal>
  </section>
</template>

<style lang="scss">
.onboarding-modal {
  &__bg {
    background: url('https://lahaus.imgix.net/static/lh-landing/background-properties.png?compress,enhance,format&width=1980=&height=950') no-repeat;
  }

  & .app-modal-content {
    @apply max-w-374 relative rounded-16;

    & #roomie-modal-navigation-button {
      @apply hidden;
    }
  }

  & .roomie-overlay {
    @apply backdrop-blur-sm bg-opacity-40;
  }

  & .roomie-button  {
    @apply font-medium;
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
